/**
 * Declaratively trackpage load metrics in React.
 *
 * We store two performance numbers for every page on our site, to
 * measure how long it takes to load them:
 * 1) "Sufficiently usable": enough content is visible to the user that
 *    they can get the "important" stuff on that page done, even if it's
 *    not everything.  So for article pages, this is satisfied if the
 *    article text is rendered, even if ancillary data like the navigation
 *    sidebar or discussions are not yet rendered.
 * 2) "Fully interactive": it's now possible for a user to interact with
 *    this page fully.  This may include not only all the html being
 *    loaded from the server, but important javascript having run.  For
 *    instance, on video pages "fully interactive" isn't satisfied until
 *    the youtube js is loaded, that lets someone click on the video to
 *    watch it.
 *
 * Every page on our site should be sending analytics back to the server,
 * at render time, saying when it became sufficiently usable and when it
 * became fully interactive.  This component helps with this.
 *
 * -- FULLY INTERACTIVE
 *
 * You can go either automated or manual. The default is to mark the page
 * as fully interactive on mount. If you provide a fullyInteractive prop,
 * we will use that instead.
 *
 * -- SUFFICIENTLY USABLE
 *
 * As above, you can go either automated or manual. The default is
 * to mark the page as sufficiently usable on mount. If you provide
 * a sufficientlyUsable prop, we will use that instead.
 *
 */
import * as React from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";

import * as PageLifecycle from "./page-lifecycle.ts";
import type {TrackedPageSlug} from "./types.ts";

interface Props {
    // What page are we doing this on?  This needs to be the same
    // string that identifies the page in calls to Analytics
    // functions.  It also needs to be listed in
    // services/analytics/page_perf/tracked_page_slug.go.
    pageName: TrackedPageSlug;
    // When markFullyInteractiveOnMount is false (the default), the
    // parent must pass in this prop, and must pass in true when the
    // page becomes fully interactive.
    fullyInteractive?: boolean;
    // When markSufficientlyUsable is `other` (the default), the
    // parent must pass in this prop, and must pass in true when
    // the page becomes sufficiently usable.
    sufficientlyUsable?: boolean;
}

const PageloadMarker = ({
    pageName,
    fullyInteractive = true,
    sufficientlyUsable = true,
}: Props): null => {
    const didMarkUsable = React.useRef(false);
    const didMarkInteractive = React.useRef(false);

    useOnMountEffect(() => {
        didMarkUsable.current = sufficientlyUsable;
        didMarkInteractive.current = fullyInteractive;

        // We're interested in marking sufficiently usable for the _parent_
        // component, but componentDidMount of that component and this one
        // should fire synchronously (during the same paint) so it's okay
        // to fire from this handler instead. In pratice, the performance.now()
        // reading, which has nearly microsecond-level precision, will differ
        // by under a millisecond for two different components in the same
        // virtual DOM hierarchy mounting on the same paint.
        PageLifecycle.reportPageLifecycleTiming(pageName, {
            usable: sufficientlyUsable,
            interactive: fullyInteractive,
        });
    });

    React.useEffect(() => {
        // We only need to update the page lifecycle timing if the page
        // has not already been marked as sufficiently usable or fully
        // interactive.
        if (
            (!didMarkUsable.current && sufficientlyUsable) ||
            (!didMarkInteractive.current && fullyInteractive)
        ) {
            didMarkUsable.current = sufficientlyUsable;
            didMarkInteractive.current = fullyInteractive;
            PageLifecycle.reportPageLifecycleTiming(pageName, {
                usable: didMarkUsable.current,
                interactive: didMarkInteractive.current,
            });
        }
    }, [
        pageName,
        didMarkUsable,
        didMarkInteractive,
        sufficientlyUsable,
        fullyInteractive,
    ]);

    return null;
};

export default PageloadMarker;
